import { Button } from "../Button";
import { Heading } from "../Heading";
import { ArrowLeft } from "../icons";
import { useTheme } from "../ThemeProvider";

import React, { type ReactElement, useState, useMemo, useRef, useEffect } from "react";
import { type Text, StyleSheet, View, type ViewStyle, type LayoutChangeEvent } from "react-native";

export type PageHeaderVariant = "root" | "interior";

export interface PageHeaderProps {
  /**
   * Title text to display in the center of the header
   */
  title?: string;

  /**
   * Optional image to display in place of the title
   */
  titleImage?: ReactElement;

  /**
   * the content to the right of the title
   */
  headerRight?: ReactElement;

  /**
   * the content to the left of the title
   * Note: this is only used for interior variant as the root variant has the title on the left
   */
  headerLeft?: ReactElement;

  /**
   * the variant of the header.
   * For interior, the header will be centered.
   * For root, the header will be left-aligned.
   */
  variant?: PageHeaderVariant;

  /**
   * Show back button on the left side
   */
  showBackButton?: boolean;

  /**
   * Custom back button icon (defaults to ArrowLeft)
   */
  backButtonIcon?: ReactElement;

  /**
   * Callback when back button is pressed
   */
  onBackPress?: () => void;

  /**
   * Accessible label for the back button
   */
  backButtonAriaLabel?: string;

  /**
   * Whether the header should have a border at the bottom
   */
  showBorder?: boolean;

  /**
   * Test ID for the header container
   */
  testID?: string;

  /**
   * Content to display below the header row but above the border
   */
  contentBelow?: ReactElement;

  /**
   * Ref forwarded to the title Heading element
   */
  titleRef?: React.Ref<Text>;

  /**
   * Overrides the semantic heading level (aria-level) independently of the
   * visual text size. Useful when the heading sits inside a dialog or modal
   * where it should be announced as a higher-level heading (e.g. h2) while
   * keeping the visual appearance of a smaller heading.
   */
  headingAriaLevel?: number;

  /**
   * Hides the title from assistive technology (e.g. when the title is
   * already announced elsewhere, such as a dialog's accessible name).
   */
  accessibleHiddenTitle?: boolean;
}

export function PageHeader({
  title,
  titleImage,
  headerRight,
  headerLeft,
  variant = "interior",
  showBackButton = false,
  backButtonIcon,
  onBackPress,
  backButtonAriaLabel,
  showBorder = false,
  testID,
  contentBelow,
  titleRef,
  headingAriaLevel,
  accessibleHiddenTitle = false,
}: PageHeaderProps): ReactElement {
  const theme = useTheme();
  const [leftSectionWidth, setLeftSectionWidth] = useState(0);
  const [rightSectionWidth, setRightSectionWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState<number | undefined>(undefined);
  const backButtonRef = useRef<View | null>(null);

  useEffect(() => {
    if (showBackButton) {
      backButtonRef.current?.focus();
    }
  }, [showBackButton]);

  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          position: "relative",
          flexDirection: "column",
          minHeight: MinHeight,
          backgroundColor: theme.colors.background,
          paddingHorizontal: theme.spacing.sc2,
        },
        contentRow: {
          flexDirection: "row",
          alignItems: "flex-start",
        },
        withBorder: {
          borderBottomWidth: 1,
          borderBottomColor: theme.colors.borderWeak,
        },
        leftSection: {
          minHeight: MinHeight,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-start",
        },
        centerSection: {
          alignSelf: "stretch",
          alignItems: "flex-start",
          justifyContent: "center",
        },
        centerSectionInterior: {
          alignItems: "center",
          paddingHorizontal: theme.spacing.s1, // Small padding to prevent overlap
        },
        centerSectionRoot: {
          alignItems: "flex-start",
          paddingLeft: theme.spacing.sc3,
        },
        rightSection: {
          minHeight: MinHeight,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-end",
        },
        sideFlexFallback: {
          flex: 1,
        },
        sideContent: {
          flexDirection: "row",
          alignItems: "center",
        },
        titleContainer: {
          alignItems: "center",
        },
        titleRoot: {
          color: theme.colors.foregroundStrong,
          textAlign: "left",
        },
        titleInterior: {
          color: theme.colors.foregroundStrong,
          textAlign: "center",
        },
        iconButton: {
          paddingLeft: 12,
        },
        contentBelow: {
          paddingTop: theme.spacing.s2,
        },
      }),
    [theme],
  );

  const containerStyle: ViewStyle[] = [styles.container, showBorder && styles.withBorder].filter(
    Boolean,
  ) as ViewStyle[];

  const handleContainerLayout = (event: LayoutChangeEvent) => {
    setContainerWidth(event.nativeEvent.layout.width);
  };

  const handleLeftSectionLayout = (event: LayoutChangeEvent) => {
    setLeftSectionWidth(event.nativeEvent.layout.width);
  };

  const handleRightSectionLayout = (event: LayoutChangeEvent) => {
    setRightSectionWidth(event.nativeEvent.layout.width);
  };

  const backButton = () => {
    if (showBackButton && onBackPress) {
      return (
        <Button
          ref={backButtonRef}
          variant="text"
          iconOnly={true}
          iconBefore={<ArrowLeft color={theme.colors.foregroundStrong} />}
          onPress={onBackPress}
          ariaLabel={backButtonAriaLabel}
          sx={styles.iconButton}
          testID={testID ? `${testID}-back-button` : "page-header-back-button"}
        >
          {backButtonIcon || <ArrowLeft color={theme.colors.foregroundStrong} />}
        </Button>
      );
    }

    return null;
  };

  const isUnmeasured = containerWidth == null;

  const renderCenterSection = () => {
    switch (variant) {
      case "root":
        return (
          <View
            style={[
              styles.centerSection,
              styles.centerSectionRoot,
              {
                width: (containerWidth ?? 0) - rightSectionWidth,
              },
            ]}
            accessibilityElementsHidden={accessibleHiddenTitle}
          >
            <View style={styles.titleContainer}>
              {titleImage ? (
                titleImage
              ) : (
                <Heading
                  ref={titleRef}
                  level={4}
                  sx={styles.titleRoot}
                  {...(headingAriaLevel != null ? { "aria-level": headingAriaLevel } : {})}
                >
                  {title}
                </Heading>
              )}
            </View>
          </View>
        );
      case "interior":
        const measuredCenterStyle = isUnmeasured
          ? undefined
          : {
              marginLeft: Math.max(0, rightSectionWidth - leftSectionWidth),
              marginRight: Math.max(0, leftSectionWidth - rightSectionWidth),
              width: containerWidth - Math.max(leftSectionWidth, rightSectionWidth) * 2,
            };

        return (
          <View
            style={[styles.centerSection, styles.centerSectionInterior, measuredCenterStyle]}
            accessibilityElementsHidden={accessibleHiddenTitle}
          >
            <View style={styles.titleContainer}>
              {titleImage ? (
                titleImage
              ) : (
                <Heading
                  ref={titleRef}
                  level={3}
                  sx={styles.titleInterior}
                  {...(headingAriaLevel != null ? { "aria-level": headingAriaLevel } : {})}
                >
                  {title}
                </Heading>
              )}
            </View>
          </View>
        );
    }
  };

  return (
    <View style={containerStyle} testID={testID || "page-header"}>
      <View style={styles.contentRow} onLayout={handleContainerLayout}>
        {/* Left Section */}
        {variant === "interior" && (
          <View style={[styles.leftSection, isUnmeasured && styles.sideFlexFallback]}>
            <View style={styles.sideContent} onLayout={handleLeftSectionLayout}>
              {backButton()}
              {headerLeft}
            </View>
          </View>
        )}

        {/* Center Section - Flex-based centering that allows height expansion */}
        {renderCenterSection()}

        {/* Right Section */}
        <View
          style={[
            styles.rightSection,
            variant === "interior" && isUnmeasured && styles.sideFlexFallback,
          ]}
        >
          <View style={styles.sideContent} onLayout={handleRightSectionLayout}>
            {headerRight}
          </View>
        </View>
      </View>
      {/* Content Below - rendered below the main header row but above the border */}
      {contentBelow && <View style={styles.contentBelow}>{contentBelow}</View>}
    </View>
  );
}

const MinHeight = 44; // Minimum header height
